import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, fromEvent, map, take } from 'rxjs';
import { ProductFilter } from '../../../models/filters/product-filter';
import { EditableListItem } from '../../../models/generics/editableListItem';
import { Product } from '../../../models/products/product';
import { ProductService } from '../../../services/product.service';
import { ConfirmModalComponent } from '../../shared/confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-product-list',
  templateUrl: './product-list.component.html',
  styleUrl: './product-list.component.css'
})
export class ProductListComponent implements OnInit {
  private productService = inject(ProductService);
  private formBuilder = inject(FormBuilder);
  private breakpointObserver = inject(BreakpointObserver);
  private modalService = inject(NgbModal);
  
  bigScreen = false; 
  page: number = 1;
  pageSize: number = 10;
  collectionSize: number = 0;
  searchTerm: string = '';

  formList = this.formBuilder.group({
    productForms: this.formBuilder.array([])
  })
  
  get productForms() {
    return this.formList.get('productForms') as FormArray;
  }

  products: EditableListItem<Product>[] = [];

  ngOnInit(): void {
    this.loadProducts();    

    this.breakpointObserver.observe(['(max-width: 1500px)']).subscribe(result => {
      this.bigScreen = result.matches;
    });

    const search = document.getElementById('search');
    const keyup$ = fromEvent(search, 'keyup');
    keyup$.pipe(
      map((i:any) => i.currentTarget.value),
      debounceTime(200)
    )
    .subscribe(() => {
      this.loadProducts();
    })
  }

  loadProducts() {
    var filter: ProductFilter = {
      offset: (this.page - 1) * this.pageSize,
      amount: this.pageSize,
      searchAll: this.searchTerm,
      name: null,
      description: null,
      id: null
    }

    this.productService.getProducts(filter).pipe(take(1)).subscribe(products => {
      this.collectionSize = products.realCount;
      this.products = products.list.map((product: Product) => {
        return {
          listItem: product,
          isEditing: false
        };
      });
      
      this.buildFormList();
    });
  }

  pageChange($event: any) {
    this.page = $event;
    this.loadProducts();
  }

  deleteProduct(id: string) {
    const modalRef = this.modalService.open(ConfirmModalComponent);
    modalRef.componentInstance.title = 'Product verwijderen';
    modalRef.componentInstance.message = 'Weet je zeker dat je dit product wilt verwijderen?';
    modalRef.componentInstance.confirmText = 'Verwijderen';
    modalRef.componentInstance.cancelText = 'Annuleren';

    modalRef.result.then((confirmed) => {
      if (confirmed) {
        this.productService.deleteProduct(id).pipe(take(1)).subscribe(() => {
          this.loadProducts();
        });
      }
    }, () => {
      // Modal dismissed/cancelled - do nothing
    });
  }

  editProduct(index: number) {
    this.productForms.controls[index].get('isEditing').setValue(true);
  }

  saveEdit(index: number) {
    const productFormGroup = this.productForms.controls[index];
    const product : Product = {
      id: productFormGroup.get('id').value,
      name: productFormGroup.get('name').value,
      price: productFormGroup.get('price').value,
      description: productFormGroup.get('description').value
    }
    if (productFormGroup.invalid) {
      productFormGroup.markAllAsTouched();
      return;
    } else {
      this.productService.updateProduct(product).subscribe(() => {
        this.loadProducts();
      });
    }
  }

  cancelEdit(index: number) {
    this.productForms.controls[index].get('isEditing').setValue(false);
    this.buildFormList();
  }

  buildFormList() {
    this.productForms.clear();
    this.products.forEach(product => {
      this.productForms.push(this.formBuilder.group({
        id: [product.listItem.id],
        name: [product.listItem.name, Validators.required],
        description: [product.listItem.description],
        price: [product.listItem.price, [Validators.required, Validators.min(0)]],
        isEditing: [product.isEditing]
      }));
    });
  }
}
